import React, { useContext, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import API from "Signups/API";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks.js";
import { SignupsAppState } from "Signups/state.js";
import { motion } from "framer-motion";

import FnrButton from "UI/FnrButton3";
import FnrConfirmationPopover from "UI/FnrConfirmationPopover";

import useHovering from "Utilities/Hooks/useHovering";

import HoverButton from "../RespondentsList/Respondent/HoverButton";
import NameWithProfileLink from "../RespondentsList/Respondent/NameWithProfileLink";
import UserSignupHistoryButton from "../RespondentsList/Respondent/UserSignupHistoryButton";

export default function CancelledRespondent({
  user,
  signupItemID,
  slotsAvailable,
  highlight,
}) {
  const { fname, name, userID, userSignupHistory } = user;

  const fnameToDisplay = fname ? fname.trim() : "this user";
  const alertMessages = {
    default: `Are you sure you want to restore ${fnameToDisplay} to the Signup?`,
    loading: `One moment, please!`,
    errors: {
      no_user: `We're having issues finding ${fnameToDisplay} to add them to the Signup.`,
      invalid_inputs: `We're having issues finding the Signup you are trying to add ${fnameToDisplay} to.`,
      no_signuplist: `We can't add ${fnameToDisplay} back. It looks like this Signup is no longer active!`,
      no_network: `Uh oh! Network not found!`,
      no_admin: `Rats! It doesn't look like you have the necessary permissions to add ${fnameToDisplay} back to the Signup.`,
      invalid_signup_item: `Uh oh! Something went wrong. We can't sign ${fnameToDisplay} up for this item.`,
      signuplist_expired: `Oops! We can't add ${fnameToDisplay} back because the Signup is now expired!`,
      no_open_slots: `Oh no! It doesn't look like there are enough available slots to add ${fnameToDisplay} back to the Signup.`,
      default: `Oh no! Something went wrong trying to add ${fnameToDisplay} to the Signup.`,
    },
    userReactivated: `Done! ${fnameToDisplay} has been added back to the Signup!`,
  };

  const [state, dispatch] = useContext(SignupsAppState);
  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();
  const [userRestored, setUserRestored] = useState(false);
  const [status, setStatus] = useState(false);
  const [showHistoryPopover, setShowHistoryPopover] = useState(false);
  const [alertMsg, setAlertMsg] = useState(alertMessages["default"]);
  const [showAddToSignupPopover, setShowAddToSignupPopover] = useState(false);
  const [isHovering, hoverProps] = useHovering();
  const [hasActiveHoverItem, setHasActiveHoverItem] = useState(null);
  const confirmationRef = useRef();
  const userCanEditSignup = state.user.isAdminOfAnyGroup;

  // This is used to keep the hover buttons visible if any items popover is currently open.
  useEffect(() => {
    if (showHistoryPopover || showAddToSignupPopover) {
      setHasActiveHoverItem(true);
    } else {
      setHasActiveHoverItem(false);
    }
  }, [showHistoryPopover, showAddToSignupPopover]);

  const toggleAddToSignupPopover = () =>
    setShowAddToSignupPopover(!showAddToSignupPopover);

  const handlePopoverClose = () => {
    setShowAddToSignupPopover(false);
    setStatus(null);
  };

  const addUserToSignup = () => {
    if (status === "loading") return;

    setStatus("loading");

    API.signupOtherUserForItem({
      signupListID: state.currentSignupList.ID,
      signupItemID: signupItemID,
      userID: userID,
      isReactivation: 1,
    })
      .post()
      .then((resp) => {
        getSignupItem(signupItemID);
        dispatch({
          type: "Other User Signup Updated",
          signupList: resp.signupList,
          allSignupLists: resp.allSignupLists,
        });
        handlePopoverClose();
        setUserRestored(true);
      })
      .catch((err, resp) => {
        setStatus("error");
        setAlertMsg(
          err && err.error && err.error in alertMessages.errors
            ? alertMessages.errors[err.error]
            : alertMessages.errors.default
        );
      });
  };

  return userRestored ? (
    <UserRestoredMessage>{`${fnameToDisplay} has been added back to signup`}</UserRestoredMessage>
  ) : (
    <Wrapper highlight={highlight}>
      <Respondent {...hoverProps}>
        <Name>
          <span className="fad fa-user-slash" />{" "}
          <NameWithProfileLink name={name} userID={userID} />
        </Name>

        {userCanEditSignup && (
          <Buttons>
            <UserSignupHistoryButton
              userSignupHistory={userSignupHistory}
              isHovering={isHovering || hasActiveHoverItem}
              showHistoryPopover={showHistoryPopover}
              setShowHistoryPopover={setShowHistoryPopover}
              signupItemID={signupItemID}
              user={user}
            />

            <span ref={confirmationRef} title="Re-Add to Signup">
              <HoverButton
                icon={faUserPlus}
                isHovering={isHovering || hasActiveHoverItem}
                isActive={showAddToSignupPopover}
                onClick={toggleAddToSignupPopover}
              />
            </span>
          </Buttons>
        )}
      </Respondent>

      <FnrConfirmationPopover
        show={showAddToSignupPopover}
        ref={confirmationRef}
        onClose={handlePopoverClose}
        message={
          status === "error"
            ? alertMsg
            : `Add ${fnameToDisplay} back to the Signup?`
        }
        button1={
          status === "error" || !slotsAvailable ? null : (
            <FnrButton
              color="blue"
              text="Yes please!"
              onClick={addUserToSignup}
              loading={status === "loading"}
            />
          )
        }
        style={{ maxWidth: 200, padding: 10 }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  height: 16px;
  display: flex;
  margin-top: 3px;
  background-color: ${({ highlight }) => (highlight ? "#eceff0" : null)};
  padding: 4px 8px;
  border-radius: 8px;
`;

const Respondent = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  font-size: 14px;
  cursor: pointer;

  &:hover .Toolbar {
    opacity: 1;
  }
`;

const Buttons = styled.div`
  display: flex;
`;

const UserRestoredMessage = styled.div`
  color: gray;
  font-style: italic;
`;

const Name = styled.div`
  color: #333;
  font-weight: 400;
`;
